import MediaImage from "@/components/ui/MediaImage";
import { CaseData, PropertyCardProps } from "@/cms/models";
import {
  getCaseClassification,
  getCaseUrl,
  getLabel,
  isAgricultural,
  isRental,
  squareMetersToHectares,
  formatNumber,
  isCommercial,
  isCountryEstate,
  isHomeSale,
  isInvestmentProperty,
  isLand,
  isOwnProperty,
  isVillaRaekkehusOrFritidshus,
} from "@/utils/helper";
import Link from "next/link";
import PropertyLabel from "./ui/PropertyLabel";
import { useState } from "react";
import { SpinnerSvg } from "./icons/Spinner";
import Card from "./ui/Card";

const PropertyDuoCard: React.FC<
  PropertyCardProps & {
    shadow: "none" | "little" | "lots";
    className?: string;
    lookSystem?: boolean;
    /** In a look-system card: its photo zooms by the section's motion level (src/styles/motion.css), not on its own. */
    cardPhoto?: boolean;
  }
> = ({ property, shadow, className, lookSystem, cardPhoto = false }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const imageUrl = property.images.filter((x) => x.lgUrl);
  const image1 = imageUrl.length > 0 ? imageUrl[0].lgUrl : null;
  const image2 = imageUrl.length > 1 ? imageUrl[1].lgUrl : null;

  const classifications = getCaseClassification(property);

  const label = getLabel(property);
  return (
    <Link
      aria-label={property.advertisementAddressOrDisplayName}
      onClick={() => setIsLoading(true)}
      href={getCaseUrl(property)}
      className={`flex relative h-full lg:h-[300px] justify-end ${className}`}
      data-component-type="PropertyDuoCard"
    >
      <Card
        className="grid grid-cols-1 md:grid-cols-3 w-full gap-2 p-2"
        shadow={shadow}
        lookSystem={lookSystem}
      >
        {isLoading && (
          <div className="absolute flex justify-center items-center inset-0 bg-gray-800 opacity-50 transition-opacity z-20">
            <SpinnerSvg />
          </div>
        )}

        {label && (
          <PropertyLabel
            className="re-case-label-duo"
            legacy={
              <div className="absolute z-10 max-w-full homepage-card-label-rounded-bl truncate homepage-text-sm top-0 left-0 uppercase bg-[var(--label-bg-color)] text-[var(--label-text-color)] px-4 py-2 text-xs mt-2 ml-2">
                <span>{label}</span>
              </div>
            }
          >
            {label}
          </PropertyLabel>
        )}
        <div className="overflow-hidden">
          {image1 && (
            <MediaImage
              src={image1}
              alt={property.advertisementAddressOrDisplayName}
              className="object-cover h-full w-full"
              data-re-card-photo={cardPhoto ? "" : undefined}
            />
          )}
        </div>
        <div className="overflow-hidden hidden md:block">
          {image2 && (
            <MediaImage
              src={image2}
              alt={property.advertisementAddressOrDisplayName}
              className="object-cover h-full w-full"
              data-re-card-photo={cardPhoto ? "" : undefined}
            />
          )}
        </div>
        <div className="flex flex-col gap-2 p-4 lg:p-6">
          {property.marketingTexts?.headline && (
            <p className="homepage-text-lg line-clamp-2">
              {property.marketingTexts.headline}
            </p>
          )}
          <div className="homepage-text-md">
            {property.advertisementAddressOrDisplayName}
          </div>
          <div className="homepage-text-sm flex flex-col gap-1">
            {getPropertyFactsView(property, classifications)}
          </div>
          <div className="pt-4 flex justify-end homepage-text-md">
            <div>{property.priceInfo}</div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

function getPropertyFactsView(property: CaseData, list: string[]) {
  // Grunde
  if (isLand(list)) {
    return <PlotOfLandPropertyFacts property={property} />;
  }

  // Erhverv | Investering
  if (isCommercial(list) && isInvestmentProperty(list)) {
    return <CommercialInvestmentPropertyFacts property={property} />;
  }

  // Erhverv | Eget brug (Køb)
  if (isHomeSale(list) && isCommercial(list) && isOwnProperty(list)) {
    return <CommercialOwnUseBoughtPropertyFacts property={property} />;
  }

  // Erhverv | Eget brug (Leje)
  if (isRental(list) && isCommercial(list) && isOwnProperty(list)) {
    return <CommercialOwnUseRentalPropertyFacts property={property} />;
  }

  // Landejendomme
  // Landbrug | Lystejendom
  if (isAgricultural(list) || isCountryEstate(list)) {
    return <AgriculturalPropertyFacts property={property} />;
  }

  if (isCommercial(list)) {
    return <CommercialOwnUseBoughtPropertyFacts property={property} />;
  }

  // Fallback
  return <DefaultPropertyFacts property={property} />;
}

interface PropertyFactProps {
  property: CaseData;
}

const DefaultPropertyFacts: React.FC<PropertyFactProps> = ({ property }) => {
  const showPlotArea = isVillaRaekkehusOrFritidshus(
    getCaseClassification(property),
  );

  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Boligareal</p>
          <p>
            {property.housingArea > 0 ? (
              <span>
                {formatNumber(property.housingArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      {showPlotArea && property.plotArea > 0 ? (
        <li>
          <div className="flex justify-between">
            <p>Grundareal</p>
            <p>
              <span>
                {formatNumber(property.plotArea)} m<sup>2</sup>
              </span>
            </p>
          </div>
        </li>
      ) : !showPlotArea ? (
        <li>
          <div className="flex justify-between">
            <p>Værelser</p>
            <p>
              {property.numberOfRooms > 0 ? (
                <span>{formatNumber(property.numberOfRooms)}</span>
              ) : (
                <span>-</span>
              )}
            </p>
          </div>
        </li>
      ) : null}
      <li>
        <div className="flex justify-between">
          <p>Ejendomstype</p>
          <p>
            {property.estateType ? (
              <span>{property.estateType}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

// Grunde
const PlotOfLandPropertyFacts: React.FC<PropertyFactProps> = ({ property }) => {
  return (
    <ul className="w-full mt-4 text-base">
      {property.plotArea > 0 && (
        <li>
          <div className="flex justify-between">
            <p>Grundareal</p>
            <p>
              <span>
                {formatNumber(property.plotArea)} m<sup>2</sup>
              </span>
            </p>
          </div>
        </li>
      )}
      <li>
        <div className="flex justify-between">
          <p>Ejendomstype</p>
          <p>
            {property.estateType ? (
              <span>{property.estateType}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div>&nbsp;</div>
      </li>
    </ul>
  );
};

// Erhverv | Investering
const CommercialInvestmentPropertyFacts: React.FC<PropertyFactProps> = ({
  property,
}) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Etageareal</p>
          <p>
            {property.floorArea > 0 ? (
              <span>
                {formatNumber(property.floorArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Afkast i %</p>
          <p>
            {property.rateOfReturn > 0 ? (
              <span>
                {formatNumber(property.rateOfReturn, {
                  fragments: 1,
                })}
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Ejendomstype</p>
          <p>
            {property.estateType ? (
              <span>{property.estateType}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};
// Erhverv | Eget brug (Køb)
const CommercialOwnUseBoughtPropertyFacts: React.FC<PropertyFactProps> = ({
  property,
}) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Etageareal</p>
          <p>
            {property.floorArea > 0 ? (
              <span>
                {formatNumber(property.floorArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Driftsudgifter</p>
          <p>
            {property.operatingCostsAnnually > 0 ? (
              <span>{formatNumber(property.operatingCostsAnnually)}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Ejendomstype</p>
          <p>
            {property.estateType ? (
              <span>{property.estateType}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

// Erhverv | Eget brug (Leje)
const CommercialOwnUseRentalPropertyFacts: React.FC<PropertyFactProps> = ({
  property,
}) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Etageareal</p>
          <p>
            {property.floorArea > 0 ? (
              <span>
                {formatNumber(property.floorArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Driftsudgifter</p>
          <p>
            {property.operatingCostsAnnually > 0 ? (
              <span>{formatNumber(property.operatingCostsAnnually)}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Ejendomstype</p>
          <p>
            {property.estateType ? (
              <span>{property.estateType}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

// Landejendomme
// Landbrug | Lystejendom
const AgriculturalPropertyFacts: React.FC<PropertyFactProps> = ({
  property,
}) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Boligareal</p>
          <p>
            {property.housingArea > 0 ? (
              <span>
                {formatNumber(property.housingArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      {property.plotArea > 0 && (
        <li>
          <div className="flex justify-between">
            <p>Grundareal</p>
            <p>
              <span>{squareMetersToHectares(property.plotArea)} ha</span>
            </p>
          </div>
        </li>
      )}
      <li>
        <div className="flex justify-between">
          <p>Ejendomstype</p>
          <p>
            {property.estateType ? (
              <span>{property.estateType}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

export default PropertyDuoCard;
